import { getAllRegionalOverviewResultsV2 } from '@protector/entities/regional-overview/services';
import { useQuery } from 'core/utils/data-store/tools';
import {
  useFeatureLoadTimingLifecycle,
  type FeatureLoadCompletion,
  type FeatureLoadSource
} from 'core/utils/segment/feature-load-timing.context';
import type { FeatureCollection, Geometry, Point } from 'geojson';
import { useRegionalOverviewStore } from '@features/regional-overview/regional-overview.store';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import type {
  RegionalOverviewProperties,
  RegionalOverviewQueryResponse,
  RegionalOverviewV2QueryResponse
} from './regional-overview.query.models';
import { RegionalOverviewQueryType, RegionalOverviewSource } from './regional-overview.query.models';

export { getAllRegionalOverviewResultsV2 } from '@protector/entities/regional-overview/services';

const TEN_MINUTES = 1000 * 60 * 10;

export const EMPTY_FEATURE_COLLECTION = { type: 'FeatureCollection', features: [] };

const getRegionalOverviewRouteModel = (source: RegionalOverviewSource): string =>
  source === RegionalOverviewSource.QUALIMETRE ? 'mycotoxin' : source.toLowerCase().replaceAll('_', '-');

export const getRegionalOverviewLoadSource = (hasData: boolean, isFetchedAfterMount: boolean, useMock?: boolean): FeatureLoadSource => {
  if (hasData && !isFetchedAfterMount) {
    return 'cache';
  }

  return useMock ? 'mock' : 'network';
};

export const useGetAllRegionalOverviewResultsV2 = <T extends Geometry, PropertiesType>(
  source: RegionalOverviewSource,
  referenceDate: string,
  orgId: string,
  useMock?: boolean
): RegionalOverviewV2QueryResponse<T, PropertiesType> => {
  const { setHasShiftData } = useRegionalOverviewStore();
  const { completeFeatureLoad, recordFeatureLoadCompletion } = useFeatureLoadTimingLifecycle();
  const { isLoading, isFetchedAfterMount, data, isError } = useQuery(
    [RegionalOverviewQueryType.GET_REGIONAL_OVERVIEW_RESULTS_V2, source, referenceDate, orgId, useMock],
    () => {
      return getAllRegionalOverviewResultsV2<T, PropertiesType>(source, referenceDate, useMock);
    },
    { staleTime: TEN_MINUTES }
  );

  useEffect(() => {
    setHasShiftData(data?.hasShiftData ?? false);

    return () => {
      setHasShiftData(false);
    };
  }, [data, setHasShiftData]);

  useEffect(() => {
    if (!data && !isError) {
      return;
    }

    const completion: FeatureLoadCompletion = {
      loadSource: getRegionalOverviewLoadSource(Boolean(data), isFetchedAfterMount, useMock),
      outcome: data ? 'success' : 'error',
      requestType: 'regional-overview-results'
    };
    const identity = { regionalModel: getRegionalOverviewRouteModel(source) };

    if (data) {
      recordFeatureLoadCompletion(completion, identity);
    } else {
      completeFeatureLoad(completion, identity);
    }
  }, [completeFeatureLoad, data, isError, isFetchedAfterMount, recordFeatureLoadCompletion, source, useMock]);

  if (!data) {
    return {
      features: EMPTY_FEATURE_COLLECTION as FeatureCollection<T, PropertiesType>,
      isLoading,
      isError
    };
  }

  return { features: data.features, isLoading, isError };
};

/**
 * Shape of `properties` on a QUALIMETRE-source feature returned by
 * `/v2/regional-overview/QUALIMETRE/{referenceDate}`. The BFF currently emits
 * `type: 'Field'`; the type stays `string` because the endpoint is not contract-
 * pinned and may grow additional values over time. `mycotoxinMapper` passes
 * `properties.type` through to the downstream `RegionalOverviewProperties` shape
 * unchanged, so any future value widens naturally.
 */
export interface MycotoxinFeatureProperties {
  id: string;
  name: string;
  type: string;
  values: {
    agronomic_class: string;
    blooming_date: string;
    field_size: string;
    field_zone: string;
    planting_date: string;
    previously_crop: string;
    property_name: string;
    risk: string;
    risk_value: string;
    tillage: string;
    variety: string;
  };
}

export const mycotoxinMapper = (
  data: FeatureCollection<Point, MycotoxinFeatureProperties>
): FeatureCollection<Point, RegionalOverviewProperties> => {
  return {
    type: data?.type,
    features: data.features.map(feature => ({
      type: feature.type,
      geometry: feature.geometry,
      properties: {
        agronomic_class: feature.properties.values.agronomic_class,
        blooming_date: feature.properties.values.blooming_date,
        field_size: feature.properties.values.field_size,
        field_zone: feature.properties.values.field_zone,
        id: feature.properties.id,
        name: feature.properties.name,
        planting_date: feature.properties.values.planting_date,
        previously_crop: feature.properties.values.previously_crop,
        property_name: feature.properties.values.property_name,
        risk: feature.properties.values.risk,
        risk_value: feature.properties.values.risk_value,
        tillage: feature.properties.values.tillage,
        type: feature.properties.type,
        variety: feature.properties.values.variety
      }
    }))
  };
};

const getAllQualimetreResults = async (
  referenceDate: string,
  useMock?: boolean
): Promise<FeatureCollection<Point, RegionalOverviewProperties>> => {
  const result = await getAllRegionalOverviewResultsV2<Point, MycotoxinFeatureProperties>(
    RegionalOverviewSource.QUALIMETRE,
    referenceDate,
    useMock
  );
  return mycotoxinMapper(result.features);
};

export const useGetAllRegionalOverviewQualimetreV2 = (referenceDate: string, useMock?: boolean): RegionalOverviewQueryResponse => {
  const { companyId } = useParams();
  const { completeFeatureLoad, recordFeatureLoadCompletion } = useFeatureLoadTimingLifecycle();
  const { isLoading, isFetchedAfterMount, data, isError } = useQuery(
    [RegionalOverviewQueryType.GET_REGIONAL_OVERVIEW_RESULTS_V2, RegionalOverviewSource.QUALIMETRE, referenceDate, companyId, useMock],
    () => {
      return getAllQualimetreResults(referenceDate, useMock);
    },
    { staleTime: TEN_MINUTES }
  );

  useEffect(() => {
    if (!data && !isError) {
      return;
    }

    const completion: FeatureLoadCompletion = {
      loadSource: getRegionalOverviewLoadSource(Boolean(data), isFetchedAfterMount, useMock),
      outcome: data ? 'success' : 'error',
      requestType: 'regional-overview-results'
    };
    const identity = { regionalModel: 'mycotoxin' };

    if (data) {
      recordFeatureLoadCompletion(completion, identity);
    } else {
      completeFeatureLoad(completion, identity);
    }
  }, [completeFeatureLoad, data, isError, isFetchedAfterMount, recordFeatureLoadCompletion, useMock]);

  return { data, isLoading };
};
